import React from 'react';
import { motion } from 'framer-motion';
import { Heart, Palette, Flower, Sparkles, Minus } from 'lucide-react';
import SEO from '../components/SEO';

export default function About() {
  const aboutSchema = {
    "@context": "https://schema.org",
    "@type": "AboutPage",
    "name": "Hvem er jeg - Cards by Eve",
    "description": "Mød Evelyn, den kreative sjæl bag Cards by Eve. Læs om hendes passion for håndtegnede illustrationer, lykønskningskort og kærligheden til den nordiske natur.",
    "publisher": {
      "@type": "Organization",
      "name": "Cards by Eve",
      "logo": "https://assets.auxodev.dk/assets/19777474-0f63-4a2a-88a2-aa8209d1f0a9/02690458-fa00-4a74-bd0d-c563c4b7c123.jpg"
    }
  };

  return (
    <div className="bg-white">
      <SEO 
        title="Hvem er jeg - Historien bag"
        description="Mød Evelyn, den kreative sjæl bag Cards by Eve. Læs om hendes passion for håndtegnede illustrationer, lykønskningskort og kærligheden til den nordiske natur."
        schema={aboutSchema}
      />
      {/* Hero Section */}
      <section className="relative pt-32 pb-24 overflow-hidden">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-16 items-center">
            <div className="lg:col-span-7 order-2 lg:order-1">
              <motion.div
                initial={{ opacity: 0, scale: 0.98 }}
                animate={{ opacity: 1, scale: 1 }}
                transition={{ duration: 1 }}
                className="relative"
              >
                <div className="aspect-[16/12] rounded-sm overflow-hidden shadow-2xl">
                  <img
                    src="https://assets.auxodev.dk/assets/19777474-0f63-4a2a-88a2-aa8209d1f0a9/72f2e08c-ae13-4486-84b6-1cc3d2141c59.png"
                    alt="Evelyn, grundlæggeren af Cards by Eve, i sit lyse og kreative kunstnerstudie i Højslev"
                    className="w-full h-full object-cover"
                    decoding="async"
                  />
                </div>
                <div className="absolute -bottom-12 -left-12 bg-brand-950 p-12 hidden md:block max-w-xs overflow-hidden">
                  <img
                    src="https://assets.auxodev.dk/assets/19777474-0f63-4a2a-88a2-aa8209d1f0a9/4f11e099-0d3d-4661-8950-eefbf868c9ec.jpg"
                    alt=""
                    className="absolute inset-0 w-full h-full object-cover opacity-40 grayscale"
                    loading="lazy"
                    decoding="async"
                  />
                  <p className="relative z-10 font-serif text-3xl text-white italic leading-tight">
                    "Kortene er min hyggelige, men også seriøse hobby."
                  </p>
                </div>
              </motion.div>
            </div>

            <div className="lg:col-span-5 order-1 lg:order-2 space-y-10">
              <div className="flex items-center space-x-4">
                <Minus className="w-6 h-6 text-brand-950" />
                <span className="text-brand-400 text-xs uppercase tracking-[0.3em] font-medium">
                  Historien bag
                </span>
              </div>
              <h1 className="text-6xl md:text-7xl font-serif text-brand-950 leading-[0.9]">
                Hvem er <br />
                <span className="italic text-brand-500">Evelyn?</span>
              </h1>
              <p className="text-brand-600 text-lg font-light leading-relaxed">
                Jeg er en skolelærer med en kreativ sjæl, der elsker tid til fordybelse, blomster, nye udfordringer. Jeg har en baggrund indenfor salg og marketing, og det ligger mig meget nært, at lave et produkt som jeg kan stå inde for og ikke mindst rammer noget af det som kunden gerne vil have.
              </p>
              <p className="text-brand-600 text-lg font-light leading-relaxed">
                Det gør mig inderligt glad, når mine kort står på hylderne og ofte med input fra de fleste kunder, der kommer med idéer til tendenser, farver og tekster de lige står og mangler på kortene. Det hygger jeg mig med at flette ind i sortimentet og laver også gerne specialbestillinger.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Philosophy Section */}
      <section className="py-32 bg-brand-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-16">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              className="space-y-6"
            >
              <Palette className="w-8 h-8 text-brand-950" />
              <h3 className="text-3xl font-serif text-brand-950">Håndtegnet</h3>
              <p className="text-brand-600 font-light leading-relaxed">
                Hvert eneste motiv starter som en analog skitse i min notesbog. Jeg elsker processen fra den første blyantsstreg til det færdige værk.
              </p>
            </motion.div>
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: 0.1 }}
              className="space-y-6"
            >
              <Flower className="w-8 h-8 text-brand-950" />
              <h3 className="text-3xl font-serif text-brand-950">Natur-elsker</h3>
              <p className="text-brand-600 font-light leading-relaxed">
                Inspirationen finder jeg i de vilde blomster og de skiftende årstider. Naturens farvepalette er min største læremester.
              </p>
            </motion.div>
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: 0.2 }}
              className="space-y-6"
            >
              <Sparkles className="w-8 h-8 text-brand-950" />
              <h3 className="text-3xl font-serif text-brand-950">Materiale</h3>
              <p className="text-brand-600 font-light leading-relaxed">
                Jeg går aldrig på kompromis med materialerne. Jeg bruger miljøvenligt mat kvalitets papir.
              </p>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Deep Dive Section */}
      <section className="py-32 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="space-y-16 text-center">
            <h2 className="text-5xl md:text-6xl font-serif text-brand-950 leading-tight">
              Min passion for det <br />
              <span className="italic">håndgribelige</span>
            </h2>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-12 text-left">
              <p className="text-brand-600 text-lg font-light leading-relaxed">
                I en stadig mere digital verden tror jeg på, at der er noget helt særligt ved at modtage et fysisk kort. Duften af papir, teksturen af trykket og den personlige hilsen skaber en forbindelse, som en skærm aldrig kan erstatte.
              </p>
              <p className="text-brand-600 text-lg font-light leading-relaxed">
                Jeg elsker især de blå og lyserøde nuancer, som man finder i naturen omkring Limfjorden – fra forglemmigej til vilde roser. Det er disse farver og former, der går igen i mine kollektioner og skaber det nordiske, stilrene udtryk.
              </p>
            </div>
            <div className="pt-12">
              <img
                src="https://assets.auxodev.dk/assets/19777474-0f63-4a2a-88a2-aa8209d1f0a9/08342fcc-dfdc-4d87-8957-87c7358e35ce.png"
                alt="Smukke håndtegnede botaniske illustrationer og blomsterkort inspireret af den nordiske natur"
                className="rounded-sm shadow-2xl w-full aspect-[21/9] object-cover"
                loading="lazy"
                decoding="async"
              />
            </div>
          </div>
        </div>
      </section>

      {/* Quote Section */}
      {/* Quote Section */}
       <section className="py-24 bg-brand-50 text-brand-950 relative overflow-hidden">
        <img
          src="https://assets.auxodev.dk/assets/19777474-0f63-4a2a-88a2-aa8209d1f0a9/4f11e099-0d3d-4661-8950-eefbf868c9ec.jpg"
          alt=""
          className="absolute inset-0 w-full h-full object-cover opacity-15"
          loading="lazy"
          decoding="async"
        />

        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center relative">
          <motion.div
            initial={{ opacity: 0, scale: 0.95 }}
            whileInView={{ opacity: 1, scale: 1 }}
            viewport={{ once: true }}
            className="space-y-12"
          >
            <Heart className="w-12 h-12 mx-auto text-brand-500" />
            <blockquote className="text-4xl md:text-6xl font-serif leading-tight italic">
              "At skabe noget med hænderne er for mig den ultimative form for ro. Jeg håber, at mine kort kan bringe den samme ro og glæde videre."
            </blockquote>
            <div className="flex items-center justify-center space-x-4">
              <div className="h-px w-12 bg-brand-200"></div>
              <cite className="text-brand-500 text-sm uppercase tracking-widest font-medium not-italic">
                Evelyn, Grundlægger
              </cite>
              <div className="h-px w-12 bg-brand-200"></div>
            </div>
          </motion.div>
        </div>
      </section>

    </div>
  );
}
